/*
 * Copyright (c) 2026, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import clsx from "clsx"
import type { ComponentProps } from "react"

import type { ValueOf } from "../../../utils/types"

import "./button.css"

const ButtonVariant = {
    Primary: "primary",
    Secondary: "secondary",
    Success: "success",
    Danger: "danger",
    Warning: "warning",
    Info: "info",
    Light: "light",
    Dark: "dark"
} as const

type ButtonVariant = ValueOf<typeof ButtonVariant>

const ButtonSize = {
    Small: "sm",
    Medium: "md",
    Large: "lg"
} as const

type ButtonSize = ValueOf<typeof ButtonSize>

interface ButtonProps extends ComponentProps<"button"> {
    /**
     * Button type.
     *
     * @default "button"
     */
    type?: "button" | "submit" | "reset"
    /**
     * Bootstrap colour variant.
     *
     * @default "primary"
     */
    variant?: ButtonVariant
    /**
     * Renders the outline version of the variant.
     *
     * @default false
     */
    outline?: boolean
    /**
     * Bootstrap size. `md` is Bootstrap's own default and adds no class.
     *
     * @default "md"
     */
    size?: ButtonSize
    /**
     * Shows a spinner over the label and blocks the button while an action is
     * in progress. The label keeps its space, so the width does not change.
     *
     * @default false
     */
    busy?: boolean
}

/**
 * The native button with the Bootstrap looks, plus a busy state.
 *
 * A busy button is disabled to prevent repeated activation, including implicit
 * submission via Enter. The native button behavior handles the duplicate-submit
 * case, so consumers do not need a separate guard in their submit handler.
 */
export const Button = ({
    busy = false,
    children,
    className,
    disabled,
    outline = false,
    size = ButtonSize.Medium,
    type = "button",
    variant = ButtonVariant.Primary,
    ...buttonProps
}: ButtonProps) => (
    <button
        {...buttonProps}
        type={type}
        className={clsx(
            "btn",
            `btn-${outline ? "outline-" : ""}${variant}`,
            { [`btn-${size}`]: size !== ButtonSize.Medium },
            className
        )}
        disabled={disabled || busy}
        aria-busy={busy}
    >
        {busy && <span className="spinner-border spinner-border-sm" data-testid="button-spinner" aria-hidden="true" />}
        <span className="button-label">{children}</span>
    </button>
)
